import styled from '@emotion/styled';
import { CLASSROOM_TYPE, useUser } from '@innovamat/ga-features';
import {
  Modal,
  Select,
  Typography,
  snack,
} from '@innovamat/glimmer-components';
import type { Student } from '@innovamat/glow-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClassrooms } from '../../../hooks/use-classrooms';
import { useRecoverStudent } from '../../../hooks/use-recover-student';

type Props = {
  student: Student | undefined;
  isOpen: boolean;
  onClose: () => void;
};

const Content = styled.div`
  margin-top: 32px;
`;

function RecoverStudentModal({ isOpen, onClose, student }: Props): JSX.Element {
  const { t } = useTranslation();
  const {
    classroomsByStage,
    classroomToOption,
    filterClassroomByDigitalPractice,
  } = useClassrooms({ mine: false });

  const studentClassroomId = student?.enrollments?.find(
    (e) => e?.classroomType === CLASSROOM_TYPE.CLASSROOM
  )?.classroomId;

  const studentCourseOrder = student?.courseOrder;

  const [selectedClassroomId, setSelectedClassroomId] = useState<
    string | undefined
  >(undefined);
  const queryClient = useQueryClient();
  const { user } = useUser();

  const { isPending, onRecoverStudent, isError } = useRecoverStudent({
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['Organization', { id: user?.organizationId }],
      });
      queryClient.refetchQueries({ queryKey: ['SchoolStudents'] });
      onClose();
    },
    onError: (errors) => {
      errors.response.errors.forEach((error) => {
        if (error.extensions?.response?.['status'] === 409) {
          snack.error(t('students.alert.maxStudentsLength'));
        }
      });
    },
  });

  const filterGoogleClassrooms = true;

  const classroomsOptions = useMemo(() => {
    return classroomsByStage(studentCourseOrder!, filterGoogleClassrooms)
      .filter(filterClassroomByDigitalPractice)
      .map(classroomToOption);
  }, [classroomsByStage, studentCourseOrder, filterGoogleClassrooms]);

  // TODO: esta funcion estaba dentro de useClassrooms pero en este
  // caso tiene que ver si existe dentro de las clases filtradas
  // ver con Sergio como organizamos este tipo de casos con los modulos.
  const classroomExists = (id: string): boolean => {
    return classroomsOptions.some((classroom) => classroom?.value === id);
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedClassroomId(undefined);
    }

    if (studentClassroomId && classroomExists(studentClassroomId)) {
      setSelectedClassroomId(studentClassroomId);
    }
  }, [isOpen, studentClassroomId]);

  const handleSubmit = (): void => {
    if (!student) return;
    const classroomChanged = student?.classroomId !== selectedClassroomId;
    if (classroomChanged)
      onRecoverStudent({ student, classroomId: selectedClassroomId });
    else onRecoverStudent({ student });
  };

  return (
    <Modal
      title={t('students.table.recover')}
      onClose={onClose}
      isOpen={isOpen}
      buttons={[
        {
          children: t('common.recover'),
          'aria-label': t('common.recover'),
          onClick: handleSubmit,
          variant: 'accent',
          role: 'button',
          loading: isPending && !isError,
          disabled:
            !selectedClassroomId || !classroomExists(selectedClassroomId),
        },
        {
          children: t('common.notNow'),
          'aria-label': t('common.notNow'),
          variant: 'tertiary',
          onClick: onClose,
          role: 'button',
        },
      ]}
      onSubmit={isPending ? undefined : handleSubmit}
    >
      <>
        <Typography.Body1>{t('students.recover.subtitle')}</Typography.Body1>
        <Content>
          <Select
            menuPortalTarget={document.body}
            labelText={t('select.student.classroom')}
            options={classroomsOptions}
            onChange={(value) => setSelectedClassroomId(value?.value)}
            value={selectedClassroomId}
          />
        </Content>
      </>
    </Modal>
  );
}

export { RecoverStudentModal };
